import { useEffect } from 'react';

/**
 * Sets --biz-zoom on the root: how much wider than 1440px the viewport is,
 * as a factor (1 up to 1440, 1.3333 at 1920, capped at 6). Called ONCE, from
 * Layout, so it holds on every route and is never torn down by a page
 * leaving.
 *
 * Who reads it: the site header, on every page (navbar.css) — so the nav
 * has the same proportions on /pricing or /blogs as on /enterprise at any
 * width; and the .biz-page layouts (/enterprise, /partners,
 * /products/ai-assistant) with their footer, which are designed at 1440px
 * and scale their whole content up in proportion (business.css) so every
 * block keeps the look it has at 1440 on a 2K, 4K or 8K screen — and a
 * browser zoomed OUT, which only makes the viewport wider in CSS pixels,
 * shows the same page at the same size instead of a small strip of small
 * type down the middle. The other pages' content keeps its own 1200px
 * column and does not scale.
 */
const DESIGN_WIDTH = 1440;
const MAX_ZOOM = 6;

const usePageScale = () => {
    useEffect(() => {
        const root = document.documentElement;
        const apply = () => {
            const zoom = Math.max(1, Math.min(MAX_ZOOM, window.innerWidth / DESIGN_WIDTH));
            root.style.setProperty('--biz-zoom', zoom.toFixed(4));
        };
        apply();
        window.addEventListener('resize', apply);
        return () => { window.removeEventListener('resize', apply); root.style.removeProperty('--biz-zoom'); };
    }, []);
};

export default usePageScale;
